import { useTexture } from "@react-three/drei";
import {
  DoubleSide,
  MeshMatcapMaterial,
  sRGBEncoding,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
} from "three";
import { useEffect, useState } from "react";
import { useViewSettings } from "@/view-settings";
import { useSelector } from "@legendapp/state/react";
import { loadingProgressTunnel } from "../state";
import { LoadingProgress } from "./loading-progress";
import { useGltfBinary } from "./useGltfBinary";
import { insightsApiInstance } from "@/shared/api/axios";

type StoreModelProps = {
  calibrationId: number;
};

const modelUrl = {
  simple: (calibrationId: number) =>
    `${insightsApiInstance.defaults.baseURL}/calibrations/${calibrationId}/simple_model`,
  photogrammetry: (calibrationId: number) =>
    `${insightsApiInstance.defaults.baseURL}/calibrations/${calibrationId}/photogrammetry_model`,
};

export function StoreModel({ calibrationId }: StoreModelProps) {
  const viewSettings = useViewSettings();
  const showPhotogrammetryModel = useSelector(() =>
    viewSettings.showNewModel.get()
  );
  const showStoreModel = useSelector(() => viewSettings.showStoreModel.get());
  const texture = useTexture("/matcap-texture.png");
  texture.encoding = sRGBEncoding;

  const [matcapMaterial] = useState(
    () =>
      new MeshMatcapMaterial({
        matcap: texture,
      })
  );

  const [simpleModel, simpleModelResult] = useGltfBinary({
    key: `simple-${calibrationId}`,
    url: modelUrl.simple(calibrationId),
    skip: !showStoreModel,
    traverse: (child) => {
      if (child instanceof Mesh) {
        child.material = matcapMaterial;
        child.material.side = DoubleSide;
      }
    },
  });

  const [photogrammetryModel, photogrammetryModelResult] = useGltfBinary({
    key: `photogrammetry-${calibrationId}`,
    url: modelUrl.photogrammetry(calibrationId),
    skip: !showStoreModel || !showPhotogrammetryModel,
    traverse: (child) => {
      if ("material" in child) {
        child.material = new MeshBasicMaterial({
          map: (child.material as MeshPhysicalMaterial).map,
        });
      }
    },
  });

  const isProgressVisible =
    photogrammetryModelResult.status === "loading" ||
    simpleModelResult.status === "loading";

  useEffect(() => {
    return () => {
      matcapMaterial.dispose();
      texture.dispose();
    };
  }, []);

  return (
    <>
      <group visible={showStoreModel}>
        {!!photogrammetryModel && (
          <primitive
            visible={showPhotogrammetryModel}
            object={photogrammetryModel}
          />
        )}

        {!!simpleModel && (
          <primitive
            visible={!showPhotogrammetryModel || !photogrammetryModel}
            object={simpleModel}
          />
        )}
      </group>

      <loadingProgressTunnel.In>
        {isProgressVisible && (
          <LoadingProgress.Root>
            <LoadingProgress.Text
              title="Loading simple model"
              progress={simpleModelResult.progress}
            />
            <LoadingProgress.Text
              title="Loading photogrammetry model"
              progress={photogrammetryModelResult.progress}
            />
          </LoadingProgress.Root>
        )}
      </loadingProgressTunnel.In>
    </>
  );
}
